import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { popModal } from '@/redux/modals/actions';

interface Props {
  message?: string;
  title?: string;
  onConfirm: () => Promise<void>;
  primaryButtonText?: string;
}

const ModalPrompt = ({
  message: propMessage,
  title: propTitle,
  onConfirm,
  primaryButtonText,
}: Props) => {
  const { t } = useTranslation();
  const title = propTitle || t('prompts.unsaved_changes.title');
  const message = propMessage || t('prompts.leave_warning');
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
      dispatch(popModal());
    }
  }, [dispatch, onConfirm]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && !isLoading) {
        dispatch(popModal());
      }
    },
    [dispatch, isLoading]
  );

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost-neutral"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="destructive" onClick={handleConfirm} isLoading={isLoading}>
            {primaryButtonText || t('common.leave')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalPrompt;
